import {
  Attivazione,
  attivazioneEndpoint,
  Status,
  getStepNumber,
  getStepByNumber,
  AttivazionePrivacyConsents,
  AttivazioneGenericConsents,
  AttivazioneConsents,
  AttivazioneResponse,
  AttivazioneFeedback,
  ApprovalPromoNameAndDiscount,
} from './../models/attivazione';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { map, tap, pluck, catchError, distinctUntilChanged } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { environment } from '../../environments/environment';
import { Product, VulnerabilityType } from '../models/product';

export const APPROVAL_STATUS_OK = 'attivazione_status_ok';
export const APPROVAL_STATUS_COMPLETED = 'attivazione_status_completed';
export const APPROVAL_STATUS_SIGNED = 'attivazione_status_signed';
export const APPROVAL_STATUS_INVALID = 'attivazione_status_invalid';
export const APPROVAL_STATUS_ERROR = 'attivazione_status_error';

const defaultPrivacyConsents = {
  valid: false,
  data: {
    generalPrivacy: undefined,
    privacyComCom: undefined,
    privacyProf: undefined,
    privacyQualServ: undefined,
    privacyComTer: undefined,
    privacyProfTer: undefined,
  },
};

const defaultGenericConsents = {
  valid: false,
  data: {
    cteCondition: undefined,
    cfgCondition: undefined,
    cadastralCondition: undefined,
    clauseCondition: undefined,
    consentsCondition: undefined,
  },
};

@Injectable()
export class AlgAttivazioniService {
  public attivazione$: Observable<Attivazione>;
  public attivazioneProduct$: Observable<Product>;
  public attivazioneProductName$: Observable<string>;
  public attivazioneStatus$: Observable<string>;
  public isOpportunityLoading$: Observable<boolean>;
  public isFeedbackLoading$: Observable<boolean>;
  public isConsentsOk$: Observable<boolean>;
  public privacyConsets$: Observable<AttivazionePrivacyConsents>;
  public genericConsets$: Observable<AttivazioneGenericConsents>;
  public policiesUrl$: Observable<any>;
  public categoryNumber$: Observable<number>;
  public operatorRatingCode$: Observable<AttivazioneFeedback>;
  public promoNameAndDiscountValidity$: Observable<ApprovalPromoNameAndDiscount>;
  public otpTimeCheck$ = new ReplaySubject<any>(1);
  public vulnerabilityTypeUserSelected$ = new BehaviorSubject<VulnerabilityType>(VulnerabilityType.anagraficAge);

  private privacyConsentsSubject = new BehaviorSubject<{
    valid: boolean;
    data: AttivazionePrivacyConsents;
  }>(defaultPrivacyConsents);
  private genericConsentsSubject = new BehaviorSubject<{
    valid: boolean;
    data: AttivazioneGenericConsents;
  }>(defaultGenericConsents);
  private attivazioneResponseSubject = new ReplaySubject<AttivazioneResponse>(1);
  private opportunityLoadingSubject = new BehaviorSubject<boolean>(false);
  private feedbackLoadingSubject = new BehaviorSubject<boolean>(false);
  private ngUnsubscribe = new Subject<void>();
  private promoNameResponseSubject = new ReplaySubject<any>(1);
  private attivazionesEndpoint = `${environment.api.basePath}/activations`;
  private opportunitiesEndpoint = `${environment.api.basePath}/act_opportunities`;
  private checkOpportunitiesEndpoint = `${environment.api.basePath}/check-opportunity`;
  private promoNameEndpoint = `${environment.api.basePath}/promo-name`;
  public currentAttivazione: Observable<Attivazione>;
  private currentAttivazioneSubject: BehaviorSubject<Attivazione> = new BehaviorSubject<Attivazione>({} as Attivazione);
  private headers: HttpHeaders;
  public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public attError: BehaviorSubject<any> = new BehaviorSubject<any>({
    err: false,
    mess: '',
  });
  public currentStep: Observable<Status>;
  private currentStepSubject: BehaviorSubject<Status> = new BehaviorSubject(Status.step1);
  public istanzaGif$: Observable<number>;
  public istanzaGifSubject: BehaviorSubject<number> = new BehaviorSubject(1);

  constructor(private http: HttpClient) {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=UTF-8',
    });

    this.attivazioneStatus$ = this.attivazioneResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const attivazione = responseOk && !!response && response.attivazione;
        const attivazioneStatus = !!attivazione && !!attivazione.opportunityStatus && attivazione.opportunityStatus.toLowerCase();
        const completed = attivazione && attivazione.completed;
        switch (true) {
          case (attivazioneStatus === 'contract sent' ||
            attivazioneStatus === 'contract queried' ||
            attivazioneStatus === 'capture order details' ||
            attivazioneStatus === 'Generazione Contratto' ||
            attivazioneStatus === 'generazione contratto') &&
            completed:
            return APPROVAL_STATUS_COMPLETED;
          case (attivazioneStatus === 'contract sent' ||
            attivazioneStatus === 'contract queried' ||
            attivazioneStatus === 'capture order details' ||
            attivazioneStatus === 'Generazione Contratto' ||
            attivazioneStatus === 'generazione contratto') &&
            !completed:
            return APPROVAL_STATUS_OK;
          case attivazioneStatus === 'contract signed' || attivazioneStatus === 'contratto da verificare':
            return APPROVAL_STATUS_SIGNED;
          case attivazioneStatus === 'contract lost' || attivazioneStatus === 'closed lost' || attivazioneStatus === 'chiusa':
            return APPROVAL_STATUS_INVALID;
          default:
            return APPROVAL_STATUS_ERROR;
        }
      })
    );

    this.isOpportunityLoading$ = this.opportunityLoadingSubject.pipe(distinctUntilChanged());
    this.isFeedbackLoading$ = this.feedbackLoadingSubject.pipe(distinctUntilChanged());
    this.attivazione$ = this.attivazioneResponseSubject.pipe(
      map((response) => {
        if (!!response && Math.ceil(response.status / 100) === 2) {
          if (!response.attivazione.webStatus) response.attivazione.webStatus = Status.step1;
        }
        return (!!response && response.attivazione) || null;
      })
    );
    this.attivazioneProduct$ = this.attivazione$.pipe(map((attivazione) => (!!attivazione && attivazione.product) || null));
    this.attivazioneProductName$ = this.attivazioneProduct$.pipe(
      map((product) => {
        let name = '';
        if (product.family !== undefined) {
          if (product.duration === 'N/A') {
            name = 'Casa Agile';
            return !!product && `${name || ''} ${product.type || ''} ${product.lightRate || ''}`;
          } else {
            return !!product && `${name || ''} ${product.type || ''} ${product.duration || ''} ${product.lightRate || ''}`;
          }
        }
      })
    );
    this.isConsentsOk$ = combineLatest(
      this.privacyConsentsSubject.pipe(map((consentStatus) => consentStatus.valid)),
      this.genericConsentsSubject.pipe(map((consentStatus) => consentStatus.valid))
    ).pipe(
      map(([privacyConsentsValid, genericConsentsValid]) => privacyConsentsValid && genericConsentsValid)
      // distinctUntilChanged()
    );
    this.privacyConsets$ = this.privacyConsentsSubject.pipe(pluck('data'));
    this.genericConsets$ = this.genericConsentsSubject.pipe(pluck('data'));
    this.operatorRatingCode$ = this.attivazione$.pipe(map((attivazione) => (!!attivazione && attivazione.operatorRatingCode) || null));
    this.policiesUrl$ = this.attivazione$.pipe(
      map((attivazione) => ({
        cfgCondition: (!!attivazione && attivazione.cfgPolicyUrl) || '',
        cteCondition: (!!attivazione && attivazione.ctePolicyUrl) || '',
        generalPolicy: (!!attivazione && attivazione.generalPolicyUrl) || '',
      }))
    );
    this.categoryNumber$ = this.attivazione$.pipe(map((attivazione) => (!!attivazione && attivazione.categoryNumber) || null));

    this.currentAttivazione = this.currentAttivazioneSubject.map((attivazione) => {
      return { ...attivazione };
    });
    this.currentStep = this.currentStepSubject.asObservable();
    this.isLoading.next(false); //disattiva quando prenderai dati da SF

    this.istanzaGif$ = this.istanzaGifSubject.asObservable();
    this.promoNameAndDiscountValidity$ = this.promoNameResponseSubject.pipe(map((response) => (!!response && response.request) || null));
  } // END CONSTRUCTOR

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  //LIKE ONB PART
  private endpoint(entity: string) {
    return `${environment.api.basePath}/${entity}`;
  }

  private save(entity: string, data: any) {
    const endpoint = this.endpoint(`${entity}s/${data._id || ''}`);
    if (data._id) {
      return this.http
        .put(endpoint, data, {
          headers: this.headers,
        })
        .map((obj) => obj[entity]);
    } else {
      return this.http
        .post(endpoint, data, {
          headers: this.headers,
        })
        .map((obj) => obj[entity]);
    }
  }

  private get(entity: string, id: string) {
    const endpoint = this.endpoint(`${entity}s/${id}`);
    return this.http
      .get(endpoint, {
        headers: this.headers,
      })
      .map((obj) => obj[entity] || {});
  }

  public getAttivazione(id?: string) {
    let act = Observable.of(null);
    if (id) {
      act = this.get(attivazioneEndpoint, id);
      act.subscribe((attivazione: Attivazione) => {
        this.currentAttivazioneSubject.next(attivazione || ({} as Attivazione));
      });
    }
    return act;
  }

  public saveAttivazione2(attivazione: Attivazione, server?: boolean) {
    this.isLoading.next(true);
    this.opportunityLoadingSubject.next(true);
    if (server) {
      const data = { ...attivazione };
      this.save(attivazioneEndpoint, data).subscribe(
        (act: Attivazione) => {
          this.currentAttivazioneSubject.next(act);
          this.isLoading.next(false);
          this.opportunityLoadingSubject.next(false);
        },
        (err) => {
          this.isLoading.next(false);
          this.opportunityLoadingSubject.next(false);
          this.attError.next({
            err: true,
            mess: 'Spiacenti, sembra che ci sia qualche difficoltà. Contattaci al numero verde 800-089902',
          });
        }
      );
    } else {
      this.currentAttivazioneSubject.next(attivazione);
      this.isLoading.next(false);
    }
  }

  public setCurrentStep(step: Status) {
    this.currentStepSubject.next(step);
  }

  // OPPORTUNITY PART

  public checkOpportunity(id: string): any {
    this.opportunityLoadingSubject.next(true);
    return this.http.get(`${this.checkOpportunitiesEndpoint}/${id}`, { observe: 'response' }).pipe(
      tap(() => {
        this.opportunityLoadingSubject.next(false);
      }),
      map((response: HttpResponse<any>) => ({
        status: response.status,
        result: response.body,
      })),
      catchError((response: HttpResponse<any>) =>
        Observable.of({
          status: response.status,
          result: response.statusText,
        })
      )
    );
  }

  public fetchOpportunity(id: string): void {
    this.opportunityLoadingSubject.next(true);
    this.getOpportunity(id)
      .pipe(
        tap(() => {
          this.opportunityLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.attivazioneResponseSubject.next(response);
      });
  }

  public fetchNamePromoOpportuniy(id: string): void {
    this.opportunityLoadingSubject.next(true);
    this.getPromoNameOpportunity(id)
      .pipe(
        tap(() => {
          this.opportunityLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.promoNameResponseSubject.next(response);
      });
  }

  public saveConsents(id, consents: AttivazioneConsents, complete: boolean = false) {
    // Set function field to enable Salesforce to compute the opportunity
    this.opportunityLoadingSubject.next(true);
    this.saveOpportunity(id, {
      ...consents,
      // NOTE: De-Commenting it makes an opportunity ID not usable anymore
      function: 'SMART',
    })
      .pipe(
        tap(() => {
          this.opportunityLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.attivazioneResponseSubject.next(response);
      });
  }

  public saveFeedback(attivazioneId, rating) {
    this.feedbackLoadingSubject.next(true);
    this.updateAttivazione(attivazioneId, { operatorRatingCode: rating })
      .pipe(
        tap(() => {
          this.feedbackLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.attivazioneResponseSubject.next(response);
      });
  }

  public updatePrivacyConsents(consents: { valid: boolean; data: AttivazionePrivacyConsents }): void {
    this.privacyConsentsSubject.next(consents);
  }

  public updateGenericConsents(consents: { valid: boolean; data: AttivazioneGenericConsents }): void {
    this.genericConsentsSubject.next(consents);
  }

  private transformAttivazione(attivazione): Attivazione {
    const { _id, operatorRatingCode, ...cleanedAttivazione } = attivazione || ({} as Attivazione);
    return {
      id: _id,
      _id: _id,
      operatorRatingCode: parseInt(operatorRatingCode, 10) || null,
      ...cleanedAttivazione,
    };
  }

  private getOpportunity(id: string): Observable<AttivazioneResponse> {
    return this.http.get(`${this.opportunitiesEndpoint}/${id}`, { observe: 'response' }).pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        attivazione: this.transformAttivazione(response.body),
      })),
      catchError((response: HttpResponse<any>) =>
        Observable.of({
          status: response.status,
          attivazione: null,
        })
      )
    );
  }

  private getPromoNameOpportunity(id: string): Observable<any> {
    return this.http
      .get(`${this.promoNameEndpoint}/${id}`, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveOpportunity(id: string, data: any) {
    return this.http
      .post(`${this.opportunitiesEndpoint}/${id}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          attivazione: this.transformAttivazione(response.body),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            attivazione: null,
          })
        )
      );
  }

  private updateAttivazione(id, data) {
    return this.http
      .put(`${this.attivazionesEndpoint}/${id}`, data, {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json; charset=UTF-8',
        }),
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          attivazione: !!response.body && this.transformAttivazione(response.body.activation),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            attivazione: null,
          })
        )
      );
  }

  public saveAttivazione(data) {
    const endpoint = `${this.attivazionesEndpoint}/${data.id}`;
    console.log('saveAttivazione', data);
    console.log('endpoint', endpoint);
    if (data.id) {
      return this.http
        .put(endpoint, data, {
          observe: 'response',
          headers: new HttpHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
          }),
        })
        .pipe(
          map((response: HttpResponse<any>) => {
            return {
              status: response.status,
              attivazione: !!response.body && this.transformAttivazione(response.body.activation),
            };
          }),
          catchError((response: HttpResponse<any>) =>
            Observable.of({
              status: response.status,
              attivazione: null,
            })
          )
        );
    } else {
      return this.http
        .post(endpoint, data, {
          observe: 'response',
          headers: new HttpHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
          }),
        })
        .pipe(
          map((response: HttpResponse<any>) => ({
            status: response.status,
            attivazione: !!response.body && this.transformAttivazione(response.body.activation),
          })),
          catchError((response: HttpResponse<any>) =>
            Observable.of({
              status: response.status,
              attivazione: null,
            })
          )
        );
    }
  }

  public saveAndStoreAttivazione(attivazione, server?: boolean) {
    if (server) {
      this.saveAttivazione(attivazione)
        .pipe(
          tap(() => {
            this.opportunityLoadingSubject.next(false);
          })
        )
        .subscribe((response) => {
          this.attivazioneResponseSubject.next(response);
        });
    } else {
      this.attivazioneResponseSubject.next({
        status: 200,
        attivazione: attivazione,
      });
    }
  }

  public createAttivazione(attivazione: Attivazione) {
    this.opportunityLoadingSubject.next(true);
    if (attivazione.webStatus === Status.callme) {
      //
    }
    delete attivazione._id;
    delete attivazione.id;
    delete attivazione.opportunityId;
    if (typeof attivazione.product !== 'string') {
      if (attivazione.product) attivazione.product = attivazione.product._id;
    }
    return this.save(attivazioneEndpoint, attivazione).map((att) => {
      this.opportunityLoadingSubject.next(false);
      return att;
    });
  }

  // delete attivazione from storage and model
  public deleteIstanza(data) {
    const endpoint = `${environment.api.basePath}/delete-istanza/${data.id}`;
    return this.http
      .post(endpoint, data, {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json; charset=UTF-8',
        }),
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          result: !!response.body && response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            result: null,
          })
        )
      );
  }

  public getDocumentsAttivazioni(oppId) {
    const endpoint = `${environment.api.basePath}/attivazione-download/${oppId}`;
    // const endpoint = `http://localhost:5000/api/cgf/${data.unipol}/${data.placet}/${data.commodity}`;

    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  public getModulo2DocumentSwitchIn(oppId: string) {
    const endpoint = `${environment.api.basePath}/switchin-modulo-download/${oppId}`;
    return this.http.get(endpoint, {
      headers: this.headers,
      responseType: 'arraybuffer',
    });
  }

  // NEXT STEP

  public nextStep(attivazione: Attivazione, currentStep) {
    const currentStepNumber = getStepNumber(currentStep);
    const nextStepNumber = currentStepNumber + 1;
    const lastStepNumber = getStepNumber(attivazione.webStatus);
    const nextStep = nextStepNumber > lastStepNumber ? getStepByNumber(nextStepNumber) : attivazione.webStatus;
    this.setCurrentStep(nextStep);
    this.opportunityLoadingSubject.next(true);

    // this.saveAttivazione({
    //   ...attivazione,
    //   webStatus: nextStep
    // }, true);

    this.saveAttivazione({ ...attivazione, webStatus: nextStep })
      .pipe(
        tap(() => {
          this.opportunityLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.attivazioneResponseSubject.next(response);
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/alg-attivazioni.service.ts